import { FeeAnalysisFilterRadioButton } from "@components/buttons/fee-analysis-filter-radio";
import { FilterValue } from "@components/buttons/filter";
import {
  SHButton,
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { EyeIcon } from "@components/svgs/eye";
import { MINIMUM_DELAY_TIME } from "@constants";
import { DateFormat } from "@constants/format";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepName } from "@layouts/stepper/step-name";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { useTheme } from "@mui/material";
import {
  FeeAnalysisFilterMapOptions,
  FeeAnalysisFilterYesNoOptions,
  PlatformFeeAnalysisInvestmentMenuFilterOptions,
  PlatformFeeAnalysisMissingInvestmentFilterOptions,
} from "@pages/platform-analysis/constant";
import { RootState } from "@redux/store";
import { format } from "date-fns";
import { debounce } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

interface FeeAnalysisHeaderProps {
  stepNumber?: number;
  removeTitle?: boolean;
  disabled?: boolean;
  onChangeFilter?: (
    investmentMenuValue: FeeInvestmentMenuOption,
    feeMissingInvestmentsStyle: FeeMissingInvestmentsOption,
  ) => void;
  onChangeHeatmapMode?: (value: FeeHeatmapMode) => void;
  onChangeDisplayStyle?: (value: FeesDisplayStyle) => void;
}

export const FeeAnalysisHeader = memo(
  ({
    stepNumber = 0,
    removeTitle = false,
    disabled,
    onChangeFilter,
    onChangeHeatmapMode,
    onChangeDisplayStyle,
  }: FeeAnalysisHeaderProps) => {
    // #region Hooks
    const { palette } = useTheme();
    const {
      platformAnalysis,
      internal: {
        feeAnalysis: {
          filterInvestmentMenuValue,
          filterMissingInvestmentValue,
          hiddenIds,
          heatmapMode,
          displayStyle,
        },
      },
    } = useSelector((state: RootState) => state.platformAnalysis);

    const {
      setHiddenIds,
      setHeatmapMode,
      setDisplayStyle,
      setIsShowHidden,
      setFeeFilters,
    } = usePlatformAnalysis();
    // #endregion Hooks

    // #region State & Refs

    const investmentMenuFilter = useMemo(
      () =>
        filterInvestmentMenuValue &&
        filterInvestmentMenuValue !== FeeInvestmentMenuOption.AllPlatform
          ? [{ label: "", value: filterInvestmentMenuValue }]
          : undefined,
      [filterInvestmentMenuValue],
    );

    const missingInvestmentFilter = useMemo(
      () =>
        filterMissingInvestmentValue &&
        filterMissingInvestmentValue !== FeeMissingInvestmentsOption.AllPlatform
          ? [{ label: "", value: filterMissingInvestmentValue }]
          : undefined,
      [filterMissingInvestmentValue],
    );
    // #endregion State & Refs

    // #region Methods
    const handleOnChangeFilter = useCallback(
      (
        missingInvestmentFilters: FilterValue[] | undefined,
        investmentMenuFilters: FilterValue[] | undefined,
      ) => {
        setHiddenIds([]);
        if (!missingInvestmentFilters && !investmentMenuFilters) {
          setFeeFilters(undefined, undefined);
          if (onChangeFilter)
            onChangeFilter(
              FeeInvestmentMenuOption.AllPlatform,
              FeeMissingInvestmentsOption.AllPlatform,
            );

          return;
        }

        const investmentMenu =
          investmentMenuFilters?.[0].value ??
          FeeInvestmentMenuOption.AllPlatform;
        const missingInvestment =
          missingInvestmentFilters?.[0].value ??
          FeeMissingInvestmentsOption.AllPlatform;

        setFeeFilters(investmentMenu, missingInvestment);

        if (onChangeFilter) onChangeFilter(investmentMenu, missingInvestment);
      },
      [onChangeFilter, setFeeFilters, setHiddenIds],
    );

    const debouncedHandleOnChangeFilter = useMemo(
      () => debounce(handleOnChangeFilter, MINIMUM_DELAY_TIME),
      [handleOnChangeFilter],
    );

    // #region Methods

    // #region Render
    return (
      <SHStack
        direction={{ xs: "column", md: "row" }}
        justifyContent={"space-between"}
      >
        {/* Step Name and Date */}
        {!removeTitle && (
          <StepName name={"Fee analysis"} stepNumber={stepNumber} />
        )}

        <SHStack justifyContent={"space-between"} pb={"10px"} spacing={"25px"}>
          {!removeTitle && (
            <SHTypography
              variant="body3"
              colorVariant="third"
              textAlign={"right"}
              lineHeight="120%"
            >
              {`Data valid: ${format(
                platformAnalysis?.databaseVersion ?? new Date(),
                DateFormat,
              )}`}
            </SHTypography>
          )}

          <SHStack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            spacing={"25px"}
          >
            <SHButton
              color="primary"
              variant="text"
              size="extraMedium"
              startIcon={
                <EyeIcon
                  isActive={true}
                  fill={
                    hiddenIds.length > 0
                      ? palette.primary.main
                      : palette.grey[500]
                  }
                />
              }
              disabled={hiddenIds.length === 0 || disabled}
              onClick={() => {
                setHiddenIds([]);
                setIsShowHidden(false);
              }}
            >
              Show hidden
            </SHButton>

            <FeeAnalysisFilterRadioButton
              labelAll={"All platforms"}
              buttonTitle={"Filters"}
              filterInvestmentMenuData={
                PlatformFeeAnalysisInvestmentMenuFilterOptions
              }
              filterInvestmentMenuValue={investmentMenuFilter}
              filterMissingInvestmentData={
                PlatformFeeAnalysisMissingInvestmentFilterOptions
              }
              filterMissingInvestmentValue={missingInvestmentFilter}
              width={"275px"}
              zIndex={10}
              onChangeFilter={debouncedHandleOnChangeFilter}
              disabled={disabled}
            />

            <SHToggleButtonOptions
              options={FeeAnalysisFilterMapOptions}
              value={heatmapMode}
              disabled={disabled}
              onChange={(_, value) => {
                if (!value) return;
                setHeatmapMode(value);
                if (onChangeHeatmapMode) onChangeHeatmapMode(value);
              }}
            />

            <SHToggleButtonOptions
              options={FeeAnalysisFilterYesNoOptions}
              value={displayStyle}
              disabled={disabled}
              onChange={(_, value) => {
                if (!value) return;
                setDisplayStyle(value);
                if (onChangeDisplayStyle) onChangeDisplayStyle(value);
              }}
            />
          </SHStack>
        </SHStack>
      </SHStack>
    );
  },
);
